/**
 * SelectionOptionsBar — réglages contextuels de la sélection de zone.
 *
 * Même bandeau, même grammaire visuelle et même emplacement que celui de la
 * gomme (cf. EraserOptionsBar) : entre la barre de menus et les onglets de
 * documents, affiché seulement quand l'outil est actif.
 *
 * CE QU'ON Y MET, ET CE QU'ON N'Y MET PAS
 * La forme armée (les quatre) et l'adoucissement du bord, parce que ce sont les
 * deux seuls réglages qui changent le RÉSULTAT. Puis les deux actions qui
 * justifient l'outil : effacer l'intérieur de la zone, ou son extérieur — ce
 * dernier étant un recadrage non destructif, l'image reste entière sous le
 * masque.
 *
 * Pas de modes booléens (ajouter / soustraire une zone à la précédente) : ils
 * demandent d'accumuler des chemins et de gérer leur combinaison, pour un
 * bénéfice que l'usage n'a pas encore réclamé. « Effacer l'extérieur » couvre
 * déjà le besoin d'inversion.
 *
 * Les dimensions de la zone sont affichées en millimètres, l'unité du document :
 * c'est ce qui permet de viser une zone réglementaire au dixième.
 */
import React from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { appliquerSelection, tailleMm, NOMS_FORMES } from '../utils/selection';

// Ce que Maj fait RÉELLEMENT, forme par forme. Une infobulle unique par gabarit
// promettait « Maj pour contraindre » aux quatre, alors que le lasso libre
// ignore la touche : il empile le point brut sans jamais lire `shiftKey`.
// Une promesse d'infobulle non tenue coûte plus cher que pas d'infobulle.
const CONTRAINTE_MAJ = {
    rect: 'Maj pour un carré',
    ellipse: 'Maj pour un cercle',
    lasso: 'Tracé à main levée',
    polygon: 'Maj pour contraindre les segments à 45°',
};

const FORME_ICONES = {
    rect: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="3 2.5">
            <rect x="3.5" y="5.5" width="17" height="13" />
        </svg>
    ),
    ellipse: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="3 2.5">
            <ellipse cx="12" cy="12" rx="8.5" ry="6.5" />
        </svg>
    ),
    lasso: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5-3.6 6.5-8 6.5c-2.4 0-4.2.6-4.2 2.2" />
        </svg>
    ),
    polygon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
            <path d="M3 9.5 11 3.5l9.5 5-2.5 10L8 20.5 3 9.5Z" strokeDasharray="3 2.5" />
        </svg>
    ),
};

export default function SelectionOptionsBar() {
    const activeTool = useLabelStore((s) => s.activeTool);
    const selection = useLabelStore((s) => s.selection);
    const setSelection = useLabelStore((s) => s.setSelection);
    const marquee = useLabelStore((s) => s.marquee);
    const elements = useLabelStore((s) => s.elements);
    const selectedElementIds = useLabelStore((s) => s.selectedElementIds);

    if (activeTool !== 'marquee') return null;

    const target = elements.find(
        (e) => e.type === 'image' && selectedElementIds.includes(e.id)
    );
    const zone = marquee && target && marquee.elementId === target.id ? marquee : null;
    const dims = zone ? tailleMm(zone, target) : null;

    const effacer = async (exterieur) => {
        if (!zone || !target) return;
        const mask = await appliquerSelection(target, zone, {
            feather: selection.feather,
            exterieur,
        });
        if (!mask) return;
        const st = useLabelStore.getState();
        st.updateElement(target.id, { eraseMask: mask });
        st._pushState(exterieur ? 'Recadrage sur la sélection' : 'Effacement de la sélection');
    };

    const ACTION = 'px-2.5 py-1 text-[9px] uppercase tracking-wider rounded border transition-colors';
    const actif = 'text-[rgba(var(--forge-muted),0.8)] border-white/10 hover:text-[rgba(var(--forge-text),1)] hover:bg-white/10';
    const inerte = 'text-[rgba(var(--forge-muted),0.25)] border-white/5 cursor-not-allowed';

    return (
        <div className="shrink-0 bg-black/25 border-b border-white/10 px-4 py-1.5 flex items-center gap-4 text-[11px] font-mono">
            {/* Identité de l'outil */}
            <div className="flex items-center gap-2 shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="3 2.5" className="text-forge-accent">
                    <rect x="3.5" y="5.5" width="17" height="13" />
                </svg>
                <span className="text-[9px] uppercase tracking-widest font-bold text-forge-accent">Zone</span>
            </div>

            <div className="w-px h-4 bg-white/10 shrink-0" />

            {/* Forme de sélection */}
            <div className="flex items-center gap-2 shrink-0">
                <span className="text-[9px] uppercase tracking-widest text-[rgba(var(--forge-muted),0.55)]">Forme</span>
                <div className="flex bg-black/40 p-0.5 rounded-md border border-white/5 gap-0.5">
                    {Object.keys(FORME_ICONES).map((id) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setSelection({ shape: id })}
                            title={`${NOMS_FORMES[id]} — ${CONTRAINTE_MAJ[id]}`}
                            aria-label={NOMS_FORMES[id]}
                            aria-pressed={selection.shape === id}
                            className={`w-7 h-6 flex items-center justify-center rounded transition-colors ${
                                selection.shape === id
                                    ? 'bg-white/15 text-[rgba(var(--forge-text),1)]'
                                    : 'text-[rgba(var(--forge-muted),0.5)] hover:text-[rgba(var(--forge-text),1)] hover:bg-white/5'
                            }`}
                        >
                            {FORME_ICONES[id]}
                        </button>
                    ))}
                </div>
            </div>

            {/* Adoucissement du bord */}
            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                <span className="text-[9px] uppercase tracking-widest text-[rgba(var(--forge-muted),0.55)] shrink-0">Adoucir</span>
                <input
                    type="range"
                    min="0" max="10" step="0.1"
                    value={selection.feather}
                    onChange={(e) => setSelection({ feather: parseFloat(e.target.value) })}
                    aria-label="Adoucissement du bord de la sélection en millimètres"
                    className="w-24 min-w-[60px] shrink accent-[rgb(var(--forge-accent))] cursor-pointer"
                />
                <input
                    type="number"
                    min="0" max="10" step="0.1"
                    value={selection.feather}
                    onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isNaN(v)) setSelection({ feather: Math.min(10, Math.max(0, v)) });
                    }}
                    aria-label="Adoucissement du bord"
                    className="w-12 shrink-0 bg-black/40 border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-[rgba(var(--forge-text),1)] focus:border-forge-accent/50 focus:outline-none"
                />
                <span className="text-[9px] text-[rgba(var(--forge-muted),0.45)] shrink-0">mm</span>

                {/* Dimensions de la zone tracée — la précision au dixième est ce
                    qui permet de viser une mention réglementaire. */}
                {dims && (
                    <span className="ml-2 shrink-0 text-[9px] text-forge-accent/90 tabular-nums">
                        {dims.w.toFixed(1)} × {dims.h.toFixed(1)} mm
                    </span>
                )}
            </div>

            {/* Actions sur la zone */}
            <div className="flex items-center gap-2 shrink-0">
                {target ? (
                    <>
                        <button
                            type="button"
                            onClick={() => effacer(false)}
                            disabled={!zone}
                            title="Effacer les pixels situés dans la zone (Suppr)"
                            className={`${ACTION} ${zone ? actif : inerte}`}
                        >
                            Effacer dedans
                        </button>
                        <button
                            type="button"
                            onClick={() => effacer(true)}
                            disabled={!zone}
                            title="Effacer tout ce qui est HORS de la zone — recadrage non destructif"
                            className={`${ACTION} ${zone ? actif : inerte}`}
                        >
                            Effacer dehors
                        </button>
                        <button
                            type="button"
                            onClick={() => useLabelStore.getState().clearMarquee()}
                            disabled={!zone}
                            title="Abandonner la zone tracée (Échap)"
                            className={`${ACTION} ${zone ? actif : inerte}`}
                        >
                            Désélectionner
                        </button>
                    </>
                ) : (
                    <span className="text-[9px] text-amber-400/80 uppercase tracking-wider">
                        Sélectionnez un calque image
                    </span>
                )}
            </div>
        </div>
    );
}
